// The end-of-game modal: ui-shell's game-state overlay in its `game_over`
// state, with chess wording (who won, and how).
import { createGameStateOverlay, type GameStateOverlayHandle, type GameStateOverlayLabels } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import type { Outcome, Termination } from '../game/outcome.js';
import type { Color } from '../game/types.js';
import type { en } from '../i18n/locales/en.js';
import type { GameRecord } from '../history/types.js';
import { createExportRow } from '../ui/exportRow.js';

type TerminationKey = keyof typeof en.game.termination;

export interface GameOverView {
  readonly outcome: Outcome;
  /** The human's colour vs the bot; null for hot-seat (results read "White wins"). */
  readonly perspective: Color | null;
  /** The finished game, when it was recorded: the dialog offers its PGN and FEN. */
  readonly record?: GameRecord | null;
}

const TERMINATION_KEYS: Readonly<Record<Termination, TerminationKey>> = {
  checkmate: 'checkmate',
  stalemate: 'stalemate',
  insufficient: 'insufficient',
  threefold: 'threefold',
  'fifty-move': 'fiftyMove',
  resign: 'resign',
  agreement: 'agreement',
  flag: 'flag',
};

export function sideLabel(color: Color): string {
  return t(color === 'w' ? 'narration.sides.white' : 'narration.sides.black');
}

export function resultTitle(view: GameOverView): string {
  const { winner } = view.outcome;
  if (winner === null) return t('game.gameOver.draw');
  if (view.perspective === null) return t('game.gameOver.sideWins', { side: sideLabel(winner) });
  return winner === view.perspective ? t('game.gameOver.win') : t('game.gameOver.loss');
}

export function terminationText(termination: Termination): string {
  return t(`game.termination.${TERMINATION_KEYS[termination]}`);
}

export class GameOverHost {
  private handle: GameStateOverlayHandle | null = null;
  private view: GameOverView | null = null;

  constructor(private readonly actions: { readonly playAgain: () => void; readonly menu: () => void }) {}

  private labels(): GameStateOverlayLabels {
    const view = this.view;
    const title = view === null ? '' : resultTitle(view);
    const body = view === null ? '' : terminationText(view.outcome.termination);
    const none = { title: '' };
    return {
      opponentDisconnected: () => none,
      reconnectingSelf: () => none,
      gameDisconnected: () => none,
      rematchOffered: () => none,
      rematchRequested: () => none,
      rematchDeclined: () => none,
      gameOver: () => ({ title, body }),
      buttons: {
        leave: t('game.gameOver.menu'),
        rematch: t('game.gameOver.playAgain'),
        'return-to-menu': t('game.gameOver.menu'),
        cancel: t('promotion.cancel'),
        accept: t('game.draw.accept'),
        decline: t('game.draw.decline'),
      },
    };
  }

  show(view: GameOverView): void {
    this.view = view;
    this.handle ??= createGameStateOverlay(() => this.labels(), {
      onAction: (id) => {
        this.close();
        if (id === 'rematch') this.actions.playAgain();
        else this.actions.menu();
      },
    });
    const result = view.outcome.winner === null ? 'draw' : view.perspective === null || view.outcome.winner === view.perspective ? 'win' : 'loss';
    const extraContent = view.record == null ? null : createExportRow(view.record);
    this.handle.update({ state: 'game_over', opponentName: '', gracePeriodEndsAt: null, gameOverResult: result, extraContent });
  }

  isOpen(): boolean {
    return this.handle?.isOpen() ?? false;
  }

  close(): void {
    this.handle?.close();
    this.handle = null;
  }
}
